/**
 * FieldPickerPopover Component
 *
 * Rich popover for selecting fields with tree view, search, and grouping.
 * Supports both single-source (Alert Rules) and multi-source (Routing Rules) modes.
 *
 * @example Simple mode (Alert Rules)
 * ```tsx
 * <FieldPickerPopover
 *   value={selectedField}
 *   onChange={setSelectedField}
 *   fields={fields}
 *   groups={groups}
 *   placeholder="Select field..."
 * />
 * ```
 *
 * @example Multi-source mode (Routing Rules)
 * ```tsx
 * <FieldPickerPopover
 *   value={selectedField}
 *   onChange={setSelectedField}
 *   sources={sources}
 *   placeholder="Select field..."
 * />
 * ```
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FieldSearch } from './FieldSearch';
import { FieldTree } from './FieldTree';
import { SourcesSidebar } from './SourcesSidebar';
import { getFieldByName, getFlatFieldList } from './utils';
import type { FieldPickerPopoverProps } from './types';

export function FieldPickerPopover({
  value,
  onChange,
  fields: simpleFields,
  groups: simpleGroups,
  sources,
  placeholder,
  searchPlaceholder,
  disabled = false,
  className,
  side = 'bottom',
  align = 'start',
  width = 400,
  displayValue,
  pinnedOptions,
}: FieldPickerPopoverProps) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder =
    placeholder ?? t('fieldPicker.selectField', 'Select field...');
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? t('fieldPicker.searchFields', 'Search fields...');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [collapsedGroups] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Multi-source state
  const [selectedSourceId, setSelectedSourceId] = useState<string>(
    sources?.[0]?.id || ''
  );

  // Determine mode and get active fields/groups
  const isMultiSource = Boolean(sources && sources.length > 0);

  const { activeFields, activeGroups } = useMemo(() => {
    if (isMultiSource && sources) {
      const selectedSource = sources.find((s) => s.id === selectedSourceId);
      return {
        activeFields: selectedSource?.fields || [],
        activeGroups: selectedSource?.groups || [],
      };
    }
    return {
      activeFields: simpleFields || [],
      activeGroups: simpleGroups || [],
    };
  }, [isMultiSource, sources, selectedSourceId, simpleFields, simpleGroups]);

  // Get flat list for keyboard navigation
  const flatFields = useMemo(
    () => getFlatFieldList(activeFields, activeGroups, collapsedGroups, searchQuery),
    [activeFields, activeGroups, collapsedGroups, searchQuery]
  );

  // Get selected field info
  const selectedField = useMemo(() => {
    if (!value) return null;

    // Search in active fields first
    let field = getFieldByName(activeFields, value);

    // If multi-source, search across all sources
    if (!field && isMultiSource && sources) {
      for (const source of sources) {
        field = getFieldByName(source.fields, value);
        if (field) break;
      }
    }

    return field;
  }, [value, activeFields, isMultiSource, sources]);

  // Handle field selection
  const handleSelect = useCallback(
    (fieldName: string) => {
      onChange(fieldName);
      setOpen(false);
      setSearchQuery('');
      setFocusedIndex(-1);
    },
    [onChange]
  );

  // Handle source change (multi-source mode)
  const handleSourceChange = useCallback((sourceId: string) => {
    setSelectedSourceId(sourceId);
    setSearchQuery('');
    setFocusedIndex(-1);
  }, []);

  // Reset state when popover closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setFocusedIndex(-1);
    }
  }, [open]);

  // Keep selectedSourceId valid when `sources` loads/changes asynchronously.
  // selectedSourceId is seeded once from sources?.[0]?.id at mount; a picker that
  // mounted while sources were still empty (e.g. CRM fields loading) would keep
  // an empty id and render NO fields even after sources arrive. The functional
  // updater is idempotent — it no-ops when the current id is still valid, so a
  // user's manual source choice is preserved and there is no render loop.
  useEffect(() => {
    if (!sources || sources.length === 0) return;
    setSelectedSourceId((current) =>
      sources.some((s) => s.id === current) ? current : (sources[0]?.id ?? '')
    );
  }, [sources]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev < flatFields.length - 1 ? prev + 1 : prev));
          break;

        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;

        case 'Enter':
          // A focused pinned option is a native button: let Enter press it
          // instead of picking the field the arrows highlighted.
          if ((e.target as HTMLElement).closest('[data-pinned-value]')) return;
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < flatFields.length) {
            const field = flatFields[focusedIndex];
            if (field && !field.disabled) {
              handleSelect(field.name);
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;

        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;

        case 'End':
          e.preventDefault();
          setFocusedIndex(flatFields.length - 1);
          break;
      }
    },
    [open, flatFields, focusedIndex, handleSelect]
  );

  // Get display text for trigger button
  // Priority: 1) selected field label, 2) displayValue override, 3) placeholder
  const displayText = selectedField?.label || displayValue || resolvedPlaceholder;
  const hasValue = Boolean(value && (selectedField || displayValue));

  // Calculate popover width based on mode
  const popoverWidth = isMultiSource ? width + 140 : width;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-between gap-2',
            'px-3 py-2 text-sm rounded-input min-w-[200px]',
            'border border-border bg-bg-elevated',
            'hover:bg-bg-muted transition-colors',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            hasValue ? 'text-fg' : 'text-fg-muted',
            className
          )}
          aria-label={t('fieldPicker.selectField', 'Select field')}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="truncate">{displayText}</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 flex-shrink-0 text-fg-muted transition-transform',
              open && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        side={side}
        align={align}
        className={cn('p-0')}
        style={{ width: popoverWidth }}
        onKeyDown={handleKeyDown}
      >
        <div
          ref={containerRef}
          className={cn('flex max-h-[400px]', isMultiSource ? 'flex-row' : 'flex-col')}
        >
          {/* Sources Sidebar (multi-source mode only) */}
          {isMultiSource && sources && (
            <SourcesSidebar
              sources={sources}
              selectedSourceId={selectedSourceId}
              onSelectSource={handleSourceChange}
            />
          )}

          {/* Main content area */}
          <div className="flex-1 min-w-0">
            {pinnedOptions && pinnedOptions.length > 0 && (
              <div
                className="border-b border-border p-1"
                role="group"
                data-testid="field-picker-pinned"
              >
                {pinnedOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm text-fg rounded transition-colors',
                      'hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                    )}
                    data-pinned-value={option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            {/* Search */}
            <FieldSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={resolvedSearchPlaceholder}
              onClear={() => setFocusedIndex(-1)}
            />

            {/* Field Tree - has its own max-h and scroll */}
            <FieldTree
              fields={activeFields}
              groups={activeGroups}
              selectedField={value}
              searchQuery={searchQuery}
              onSelect={handleSelect}
              focusedIndex={focusedIndex}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default FieldPickerPopover;
